<template>
  <Teleport to="body">
    <!-- Modal -->
    <Transition name="modal">
      <div v-if="showSlide" class="modal-mask">
        <div class="modal-container row">
          <div class="col modal-header order-md-1 order-2 row-title">
            <div v-if="!loading"
              class="row d-flex justify-content-center align-items-center w-100"
            >
              <div class="order-lg-1 order-1 col-lg-2 col-3">
                <div class="text-center title left-part">
                  {{ route.params.language == "pt-br" ? "Parte" : "Part" }}
                  {{ partLabel + 1 }}
                </div>
              </div>
              <div class="order-lg-2 order-2 col-lg-2 col-3">
                <div class="text-center title">
                  {{ sessionLabel }}                  
                </div>
              </div>
              <div class="order-lg-3 order-4 col-lg-4 col-12 pt-2 pt-lg-0 d-flex justify-content-center align-items-center">
                <div class="col-4 d-flex justify-content-end">
                  <div class="div-prev m-top" @click="change">
                    <div class="font-prev">
                      <font-awesome
                        :icon="['fas', 'angle-left']"
                        size="md"
                        class="cursor-pointer color-icon"
                      />
                    </div>
                  </div>
                </div>
                <div class="col-4 text-center title">
                  {{ (props.offsetInPart ?? 0) + idxCurrent + 1 }} / {{ props.totalInPart ?? (total + 1) }}
                </div>
                <div class="col-4 d-flex justify-content-start">
                  <div class="div-next m-top" @click="change">
                    <div class="font-next">
                      <font-awesome
                        :icon="['fas', 'angle-right']"
                        size="md"
                        class="cursor-pointer color-icon"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="order-lg-4 order-3 col-lg-4 col-6 d-flex justify-content-end align-items-center">
                <div
                  v-if="isMobile()"
                  class="row super-zoom-row d-flex justify-content-center align-items-center"
                >
                  <label class="super-zoom-label">
                    <input
                      type="checkbox"
                      class="super-zoom-input"
                      v-model="superZoom"
                    />
                    <span class="super-zoom-custom">
                      <font-awesome
                        v-if="superZoom"
                        :icon="['fas', 'check']"
                        class="super-zoom-check-icon"
                      />
                    </span>
                    <span class="super-zoom-text">{{ translate.superZoomLabel }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div class="col modal-body order-md-2 order-1">
            <div class="row">
              <div class="col-12 d-flex justify-content-center">
                <button
                  type="button"
                  class="btn-mobile"
                  @click="sair"
                  aria-label="Close"
                >x</button>
                <div class="text-center mt-5 p-5 div-loading" v-if="loading">
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
                <div
                  v-else
                  ref="zoomWrapperRef"
                  class="super-zoom-frame-wrapper"
                  :class="{ 'super-zoom-frame': zoomActive && isMobile() }"
                  @touchstart="onZoomTouchStart"
                  @touchmove="onZoomTouchMove"
                  @touchend="onZoomTouchEnd"
                  @touchcancel="onZoomTouchEnd"
                >
                  <img
                    :src="img"
                    width="1280"
                    height="1920"
                    :alt="modalAltText"
                    :style="
                      isZooming && zoomActive && isMobile()
                        ? {
                            transform: `scale(${ZOOM_FACTOR})`,
                            transformOrigin: `${zoomX}% ${zoomY}%`,
                            transition: 'transform-origin 0.05s linear',
                          }
                        : {
                            transform: 'scale(1)',
                            transformOrigin: 'center center',
                            transition: 'transform 0.2s ease',
                          }
                    "
                  />
                  <div v-if="showSuperZoomTooltip" class="super-zoom-tooltip">
                    <button
                      type="button"
                      class="super-zoom-tooltip-close"
                      @click="closeSuperZoomTooltip"
                      aria-label="Fechar"
                    >
                      <font-awesome :icon="['fas', 'xmark']" />
                    </button>
                    <div class="super-zoom-tooltip-text">
                      {{ translate.superZoomTooltipModal }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
import { isMobile } from "@/shared/utils/mobile";

const props = defineProps([
  "showSlide",
  "imgSlide",
  "translate",
  "total",
  "session",
  "loading",
  "sessionLabel",
  "partLabel",
  "idxCurrent",
  "slidePlayZoom",
  "seconds",
  "keyDownEvents",
  "imageAltText",
  "offsetInPart",
  "totalInPart",
]);
const emits = defineEmits([
  "showModalModelSlideShow",
  "changePhoto",
  "changeAutomatic",
  "showModalSubscribePlans",
  "updateSlidePlayZoom",
  "updateSeconds",
  "changeSession",
]);
const img = computed(() => props.imgSlide);
const current = computed(() => props.idxCurrent);
const interval = ref<any>(null);
const hover = ref(false);

const superZoom = ref(false);
const showSuperZoomTooltip = ref(false);
const zoomActive = ref(false);
const isZooming = ref(false);
const zoomX = ref(50);
const zoomY = ref(50);
const ZOOM_FACTOR = 3.0;
const zoomWrapperRef = ref<HTMLElement | null>(null);

watch(superZoom, (val) => {
  if (val) {
    showSuperZoomTooltip.value = true;
    zoomActive.value = false;
  } else {
    showSuperZoomTooltip.value = false;
    zoomActive.value = false;
    isZooming.value = false;
  }
});

function closeSuperZoomTooltip() {
  showSuperZoomTooltip.value = false;
  zoomActive.value = true;
}

function updateZoomPosition(clientX: number, clientY: number, target: HTMLElement) {
  const rect = target.getBoundingClientRect();
  zoomX.value = ((clientX - rect.left) / rect.width) * 100;
  zoomY.value = ((clientY - rect.top) / rect.height) * 100;
}

function isTouchInsideWrapper(clientX: number, clientY: number) {
  const el = zoomWrapperRef.value;
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return (
    clientX >= rect.left &&
    clientX <= rect.right &&
    clientY >= rect.top &&
    clientY <= rect.bottom
  );
}

function onZoomTouchStart(e: TouchEvent) {
  if (!zoomActive.value || !isMobile()) return;
  const touch = e.touches[0];
  if (!touch) return;
  const wrapperEl = zoomWrapperRef.value;
  if (!wrapperEl) return;
  if (!isTouchInsideWrapper(touch.clientX, touch.clientY)) return;
  updateZoomPosition(touch.clientX, touch.clientY, wrapperEl);
  isZooming.value = true;
}

function onZoomTouchMove(e: TouchEvent) {
  if (!zoomActive.value || !isMobile()) return;
  e.preventDefault();
  const touch = e.touches[0];
  if (!touch) return;
  const wrapperEl = zoomWrapperRef.value;
  if (!wrapperEl) return;
  if (!isTouchInsideWrapper(touch.clientX, touch.clientY)) {
    isZooming.value = false;
    return;
  }
  updateZoomPosition(touch.clientX, touch.clientY, wrapperEl);
  isZooming.value = true;
}

function onZoomTouchEnd() {
  isZooming.value = false;
}

watch(
  () => props.idxCurrent,
  () => {
    isZooming.value = false;
    zoomX.value = 50;
    zoomY.value = 50;
  }
);

const modalAltText = computed(() => {
  const base = props.imageAltText ?? (`Part ${props.partLabel + 1}`);
  return `${base} - Zoom | ${props.sessionLabel} | Img ${props.idxCurrent + 1}`;
});
watch(
  () => props.slidePlayZoom,
  (val) => {
    if (val == true) {
      startPlay();
    } else {
      clearInterval(interval.value);
    }
  }
);

watch(
  () => props.keyDownEvents,
  (val) => {
    if (val) {
      window.addEventListener("keydown", handleKeydownZoom);
    } else {
      window.removeEventListener("keydown", handleKeydownZoom);
    }
  }
);

const storeUser = userStore();
const route = useRoute();
function change(eventChange: any) {
  //Avançar
  if (
    current.value === props.total &&
    eventChange.target.closest("div.div-next") != null
  ) {
    if (!storeUser.isActiveUser) {
      emits("showModalModelSlideShow", false);
      emits("showModalSubscribePlans", true);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "paywall",
        paywall: {
          acionador:
            "Ao finalizar seção gratis e avançar para uma seção de fotos bloqueada - Modo SlideShow",
        },
      });
    } else {
      emits("changeAutomatic", true);
    }
    return false;
  }
  //Voltar
  if (
    current.value === 0 &&
    eventChange.target.closest("div.div-prev") != null
  ) {
    if (!storeUser.isActiveUser) {
      emits("showModalModelSlideShow", false);
      emits("showModalSubscribePlans", true);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "paywall",
        paywall: {
          acionador:
            "Ao retornar de uma seção gratis e retornar para uma seção de fotos bloqueada - Modo SlideShow",
        },
      });
    } else {
      emits("changeAutomatic", false);
    }
    return false;
  }

  let changeCurrrent = current.value;
  if (eventChange.target.closest("div.div-next") != null) changeCurrrent++;
  else changeCurrrent--;

  emits("changePhoto", changeCurrrent);
}
async function sair() {
  superZoom.value = false;
  await emits("changeSession", {
    part: props.partLabel,
    session: props.session,
    next: true,
    zoom: current.value,
  });
  await emits("showModalModelSlideShow", false);
}
function startPlay() {
  clearInterval(interval.value);
  emits("updateSlidePlayZoom", true);
  var cont = 0;
  interval.value = setInterval(function () {
    if (document.getElementsByClassName("font-next").length && cont > 0) {
      cont = 0;
      document.getElementsByClassName("font-next")[0].click();
    } else if (document.getElementsByClassName("font-next").length) cont = 1;
  }, props.seconds * 1000);
}
function stopPlay() {
  emits("updateSlidePlayZoom", false);
}
async function minusPlay() {
  if (props.seconds > 1) {
    await emits("updateSeconds", props.seconds - 1);
    startPlay();
  }
}
async function plusPlay() {
  if (props.seconds < 10) {
    await emits("updateSeconds", props.seconds + 1);
    startPlay();
  }
}
function startStopPlay() {
  if (props.slidePlayZoom) {
    stopPlay();
  } else {
    startPlay();
  }
}
function handleKeydownZoom(e) {
  if (e.keyCode == 37) {
    //Arrow Left
    if (document.getElementsByClassName("font-prev").length) {
      document.getElementsByClassName("font-prev")[0].click();
    }
  } else if (e.keyCode == 39) {
    //Arrow Right
    if (document.getElementsByClassName("font-next").length) {
      document.getElementsByClassName("font-next")[0].click();
    }
  } 
  /*
  else if (e.keyCode == 38) {
    //Arrow Up
    e.preventDefault();
    plusPlay();
    hover.value = true;
    setTimeout(() => {
      hover.value = false;
    }, 2000);
  } else if (e.keyCode == 40) {
    //Arrow Down
    e.preventDefault();
    minusPlay();
    hover.value = true;
    setTimeout(() => {
      hover.value = false;
    }, 2000);
  } else if (e.keyCode == 13 || e.keyCode == 32) {
    //Enter or Space
    e.preventDefault();
    startStopPlay();
    hover.value = true;
    setTimeout(() => {
      hover.value = false;
    }, 2000);
  }
  */
}
</script>

<style scoped lang="css">
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  transition: opacity 0.3s ease;
  overflow-y: auto;
}

.modal-container {
  max-width: 1460px;
  width: 95%;
  margin: auto;
  padding: 0px;
  background-color: #fff;
  border-radius: 0px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
}

.modal-header {
  height: 70px;
}

.modal-body {
  margin: 0;
}

.modal-body img {
  max-width: 100%;
  object-fit: cover;
  height: auto;
}

.modal-default-button {
  float: right;
}

/*
   * The following styles are auto-applied to elements with
   * transition="modal" when their visibility is toggled
   * by Vue.js.
   *
   * You can easily play with the modal transition by editing
   * these styles.
   */

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-body {
  margin: 10px 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

.btn-close {
  opacity: 1;
}

.btn-mobile {
  top: 10px;
  right: 120px;
  position: absolute;
  z-index: 1;
  color: white;
  font-size: 60px;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}

.color-icon {
  color: #5b5858;
}

.div-loading {
  min-height: 1920px;
}

.title {
  font-family: "Roboto-Regular";
  font-size: 16px;
  top: 5px;
}

.slide-controls {
  width: 270px;
  border: 1px solid #000;
  border-radius: 30px;
}

.m-top {
  margin-top: 10px;
}

.size-seconds {
  font-size: 14px;
  color: white;
  background-color: #5b5858;
  border-radius: 50%;
  width: 27px;
  height: 27px;
  padding: 3px 6px;
}

.left-part {
  padding-left: 40px;
}

.super-zoom-frame-wrapper {
  position: relative;
  display: inline-block;
  max-width: 100%;
}

.super-zoom-frame {
  overflow: hidden;
  cursor: zoom-in;
}

.super-zoom-frame img {
  will-change: transform, transform-origin;
}

.super-zoom-row {
  margin-top: -2px;
}

.super-zoom-label {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin: 0;
  position: relative;
  left: 15px;
}

.super-zoom-input {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  opacity: 0;
  margin: 0;
  cursor: pointer;
  z-index: 1;
}

.super-zoom-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #868686;
  border-radius: 4px;
  margin-right: 10px;
  position: relative;
  transition: background 0.15s;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.super-zoom-input:checked + .super-zoom-custom {
  background: #868686;
}

.super-zoom-check-icon {
  color: #fff;
  font-size: 12px;
  line-height: 1;
}

.super-zoom-text {
  font-family: "Roboto-Regular";
  font-weight: 600;
  color: #868686;
  font-size: 16px;
}

.super-zoom-tooltip {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 50;
  background: rgba(255, 255, 255, 0.75);
  padding: 28px 40px;
  max-width: 420px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.super-zoom-tooltip-text {
  color: #000;
  font-family: "Exo-2";
  font-weight: 500;
  font-size: 18px;
  margin-bottom: 16px;
  width: 225px;
}

.super-zoom-tooltip-close {
  position: absolute;
  top: 6px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #5b5858;
  line-height: 1;
}

/* regras mobile 769vp */
@media screen and (max-width: 769px) {
  .btn-mobile {
    font-size: 40px;
    top: -5px;
    right: 20px;
  }

  .modal-header {
    height: 60px;
  }
  .title {
    font-size: 16px;
    top: unset;
  }
  .div-loading {
    min-height: 520px;
  }
  .m-top {
    margin-top: 0px;
  }
  .left-part {
    padding-left: 0px;
  }
  .slide-controls {
    margin: 15px;
  }
  .row-title {
    height: 80px;
  }
}
</style>
